import React from "react";
import PropTypes from "prop-types";
import { Button, theme, Typography } from "antd";
import { Modal } from "ui/Modal";
import CustomDrawer from "ui/Drawer";
import { useDevice } from "hooks/useDevice";
import { CloseOutlined } from "@ant-design/icons";
import warningIcon from "assets/icons/warningIcon.svg"; // Import the SVG
import { getStyles } from "./styles";

const { Text } = Typography;
const { useToken } = theme;

export const NewConfirmModal = ({
  visible,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  icon = warningIcon,
  hasIcon = true,
  highlightText = "",
  warningText = "",
  loading = false,
  onlyPublish = false,
}) => {
  const { token } = useToken();
  const styles = getStyles(token);
  const { isMobile } = useDevice();

  const hasHighlight = Boolean(highlightText);
  const hasWarning = Boolean(warningText);
  const isPublish = confirmLabel.toLowerCase() === 'publish' || title?.toLowerCase().includes('publish');

  // The drawer renders the title in its own header, so on mobile we skip the in-body <h2>.
  const body = (
    <div>
      {!isPublish && hasIcon && (
        <div style={styles.warningIcon}>
          <img src={icon} alt="warning" width="24" height="24" />
        </div>
      )}
      <div style={styles.bodyContent}>
        {!isMobile && <h2 style={styles.modalTitle}>{title}</h2>}
        <div style={styles.contentContainer}>
          <Text style={styles.messageText}>
            {message}
            {hasHighlight && (
              <Text style={styles.listingName}> {highlightText}?</Text>
            )}
          </Text>
          {hasWarning && !isPublish && (
            <Text style={styles.warningText}>
              {warningText}
            </Text>
          )}
        </div>
        <div style={styles.buttonContainer}>
          <Button
            style={styles.cancelButton}
            onClick={onClose}
            disabled={loading}
          >
            {cancelLabel}
          </Button>
          <Button
            style={isPublish ? { ...styles.deleteButton, background: token.colorPrimary, borderColor: token.colorPrimary } : styles.deleteButton}
            type={isPublish ? 'primary' : undefined}
            onClick={onConfirm}
            loading={loading}
            disabled={loading}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <CustomDrawer open={visible} setOpen={() => onClose()} title={title}>
        {body}
      </CustomDrawer>
    );
  }

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      width={431}
      closeIcon={<CloseOutlined />}
      maskClosable={true}
      style={styles.modal}
    >
      {body}
    </Modal>
  );
};

NewConfirmModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  icon: PropTypes.string,
  /** Hides the warning circle above the title, for confirmations designed without one. */
  hasIcon: PropTypes.bool,
  highlightText: PropTypes.string,
  warningText: PropTypes.string,
  loading: PropTypes.bool,
  onlyPublish: PropTypes.bool,
};